import React, { useContext, useEffect, useState } from 'react';
import {
  AppContext,
  COMMON_CONSTANTS,
  SuperTable,
  getTranslatedText,
  saveTableRowCount
} from '@nile/components';
import nanoid from 'nanoid';
import { cloneDeep, isEmpty } from 'lodash';
import { preFormatFilterObject } from '@nile/reports/src/modules/component/helper';
import {
  EfficiencyCell,
  InsightCell,
  TicketCountCell,
  SubjectCell,
  TimeCell,
  CurrenyHourCell,
  PercentageCell
} from '@nile/reports/src/modules/EfficiencyReport/components/subpages/Cells';
import { onQuerySearch } from '@nile/reports/src/modules/EfficiencyReport/graphql/resolver';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import { useIntl } from 'react-intl';
import TableAICell from '../Cells/TableAICell';

const defaultColumnCells = {
  INSIGHT: InsightCell,
  EFFICIENCY_CELL: EfficiencyCell,
  TICKETCOUNT: TicketCountCell,
  SUBJECT: SubjectCell,
  TIME: TimeCell,
  AI: TableAICell,
  CURRENCY: CurrenyHourCell,
  CURRENCY_HOUR: CurrenyHourCell,
  PERCENTAGE: PercentageCell
};

const ROWCOUNT = 25;

function AggregationTable({
  columns = [],
  name,
  api,
  condition,
  setClient,
  minmumHeight = false,
  padded,
  showAI = false,
  clientData = null,
  advancedChart = {},
  dynamicRowCount,
  customReport
}) {
  const intl = useIntl();
  const { dataKey } = advancedChart;

  const tabContext = useContext(TicketContext);
  const appContext = useContext(AppContext);
  const { user } = appContext;
  const [loading, setLoading] = useState(null);
  const [rowCount, setRowCount] = useState(ROWCOUNT);
  const [listInfo, setListInfo] = useState({
    page: 1,
    pageSize: ROWCOUNT,
    totalCount: 0,
    sort: null
  });
  const [data, setData] = useState([]);

  const isEfficiencyTable =
    name ===
    'reports.efficiencyReport.client_resource_efficiency.efficiencyBy.ticket.category.name';

  const [columnsGroup, setColumnGroup] = useState([
    { header: 'Name', columns: [] }
  ]);

  const aiColumn = {
    header: '',
    key: 'ai-component',
    type: 'AI',
    condition: condition,
    client: clientData
  };

  const { client, query } = onQuerySearch(api);

  const { operands, reportCondition } = preFormatFilterObject({
    entity: customReport ? 'HoursWorklogEntry' : 'ClientResourceEfficiency',
    filterObj: cloneDeep(condition?.defaultCondition),
    removeLabel: true
  });

  const getAction = (cellValue) => {
    switch (true) {
      case cellValue?.type === 'INSIGHT':
        return setClient;
      case cellValue?.type === 'SUBJECT':
        return {
          onClick: tabContext.openTicket
        };
      default:
        return null;
    }
  };

  const getWidth = (cellValue) => {
    switch (cellValue?.type) {
      case 'INSIGHT':
        return 120;
      case 'EFFICIENCY_CELL':
        return 200;
      default:
        return 150;
    }
  };
  const refreshTable = (_, pageOptions = listInfo) => {
    if (!query) return;

    setLoading(true);
    let queryInput = {
      condition:
        (reportCondition &&
          !isEmpty(operands) && {
            joinOperator: 'AND',
            operands: operands || null
          }) ||
        null,
      ...condition?.filterCondition
    };
    if (condition?.hasPagination) {
      if (!condition?.nested) {
        queryInput = {
          ...queryInput,
          page: pageOptions?.page,
          pageSize: pageOptions?.pageSize,
          sort: pageOptions?.sort || null
        };
      } else {
        queryInput = {
          ...queryInput,
          pagination: pageOptions?.sort
            ? {
                page: pageOptions?.page,
                pageSize: pageOptions?.pageSize,
                sort: pageOptions?.sort || null
              }
            : {
                page: pageOptions?.page,
                pageSize: pageOptions?.pageSize
              }
        };
      }
    }

    let tableColumns = [...columns];

    if (showAI) {
      tableColumns.push(aiColumn);
    }
    const colGroups = tableColumns.map((el) => {
      const { disableSortBy = true, type = 'Text', disableFilters = true } = el;
      return {
        ...el,
        header: getTranslatedText(intl, el?.header || ' '),
        accessorKey: el?.key,
        id: el?.key || nanoid(),
        disableResizing: true,
        disableSortBy: disableSortBy,
        type: type,
        editable: false,
        disableFilters: disableFilters,
        className: 'sticky',
        size: getWidth(el),
        actions: getAction(el),
        meta: { ...el?.meta, condition },
        field: el,
        source: isEfficiencyTable ? 'EFFICIENCY_TABLE' : null
      };
    });
    setColumnGroup([{ header: 'Name', columns: colGroups }]);

    client
      .query({
        query: query,
        variables: {
          input: queryInput
        },
        fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NETWORK_ONLY
      })
      .then((res) => {
        const respData = res?.data?.[api]?.data || res?.data?.[api]?.[dataKey];
        const totalCount =
          res?.data?.[api]?.totalCount ||
          res?.data?.[api]?.listInfo?.totalCount;
        setData(respData);
        setListInfo(() => ({
          page: pageOptions?.page,
          pageSize: pageOptions?.pageSize,
          totalCount: totalCount || 0,
          sort: pageOptions?.sort || null
        }));
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    refreshTable();
  }, [
    api,
    JSON.stringify(condition?.filterCondition),
    JSON.stringify(condition?.defaultCondition)
  ]);

  return (
    <div
      className={`${isEmpty(data) ? 'position-relative min-height-320' : ''} ${
        minmumHeight ? 'min-height-320' : 'full-height'
      } mt10 scroll`}
    >
      <SuperTable
        isLoading={loading}
        noBulkSelect
        columns={columnsGroup}
        listInfo={listInfo}
        defaultColumnCells={defaultColumnCells}
        context={{}}
        data={data || []}
        rowCount={rowCount}
        tableName={name || nanoid()}
        refreshTable={refreshTable}
        fullHeight
        pagination={Math.ceil(listInfo?.totalCount / listInfo?.pageSize) > 1}
        pages={Math.ceil(listInfo?.totalCount / listInfo?.pageSize) || 0}
        activePage={listInfo?.page || 1}
        recordCount={listInfo?.totalCount || 0}
        padded={padded}
        dynamicRowCount={dynamicRowCount}
        setRowCount={setRowCount}
        saveTableRowCount={saveTableRowCount}
        user={user}
        emptyState={{
          removeMargin: true
        }}
      />
    </div>
  );
}

export default AggregationTable;
